export interface CheckpointID {
	name: string;
	exit?: boolean;
}

export interface PlayerOptions {
	loop: boolean;
	mute: boolean;
}

export interface AudioConfig {
	id: string;
	source: string;
	options: PlayerOptions;
}

/**
 * Conditional navigate config for `type: "navigate"` items.
 *
 * Compares two session record fields, each referenced as "<recordType>.<fieldPath>"
 * (field path may be nested with dots). When the resolved values are equal it navigates to
 * `onMatch`, otherwise to `onMismatch`.
 *
 * @example
 * {
 *   record: "verified-device.deviceId",
 *   match: "SessionMetadata.precapturedDeviceId",
 *   onMatch: "skip_scan",
 *   onMismatch: "scan_1"
 * }
 */
export interface ConditionalNavigateConfig {
	record: string;
	match: string;
	onMatch: string;
	onMismatch: string;
}

export type NavigateConfig = string; // ID of a page to navigate to

export interface WidgetConfig {
	type: string;
	settings?: any;
	translations?: any;
}

/**
 * Config for `type: "button"` menu items.
 */
export interface ButtonItemConfig {
	/**
	 * Render the navigate-style right-arrow icon next to the title.
	 * Useful when a `button` item should visually read as "this advances the flow"
	 * without having to use `type: "navigate"` (which short-circuits click event handling).
	 */
	showArrow?: boolean;
	subtitle?: string;
}

export type NavigatorMenuItemConfig = any | NavigateConfig | WidgetConfig | AudioConfig | ButtonItemConfig;

export interface NavigatorMenuItem<T extends NavigatorMenuItemConfig = any> {
	id: string;
	title: string;
	type:
		| "say"
		| "navigate"
		| "info"
		| "divider"
		| "content"
		| "timer"
		| "widget"
		| "audio"
		| "button"
		| "copy"
		| "link"
		| "link.incognito";
	config: T;
}

export type NavigatorVideoFacingMode = "environment" | "user";

export type NavigatorEventType =
	| "iframe_change"
	| "timer_complete"
	| "agent_connect"
	| "form_submit"
	| "form_submit_invalid"
	| "navigate"
	| "human_box_checker_audio"
	| "photo_capture_completed"
	| "photo_capture_not_supported"
	| "media_play"
	| "media_pause"
	| "media_end"
	| "click";

export type UserQueuePriority = "low" | "normal" | "high";

export interface NavigatorEvent {
	type: NavigatorEventType;
	id?: string;
	navigate?: NavigateConfig; // Where to navigate on event fire
	menu?: NavigatorMenuItem[]; // Items to add to the menu on event fire
	content?: NavigatorMenuItem[]; // Items to add to the content on event fire
	/**
	 * When the event fires, emit a Chime/Connect control message (`{{id:data}}`) to the peer session.
	 * Proctor scripts use this to signal the patient session (e.g. for the embedded host app to react);
	 * user scripts can use it symmetrically. No product-specific coupling in the navigator model.
	 */
	controlMessage?: { id: string; data?: unknown };
}

export interface NavigatorPage {
	id: string;
	title?: string;
	content?: NavigatorMenuItem[] | string;
	menu?: NavigatorMenuItem[] | string;
	events?: NavigatorEvent[];

	syncID?: string;
	allowBack?: NavigatorEvent | boolean;
	stepper?: {
		type?: "bars";
		maxSteps: number;
		currentStep: number;
	};
	checkpointID?: CheckpointID | CheckpointID[];

	isEnd?: boolean; // Indicates whether this slide is considered an end of the session and the UI may end.

	audioVideo?: {
		enableVideo?: boolean;
		enableAudio?: boolean;
		enableRecording?: boolean;
		videoFacingMode?: NavigatorVideoFacingMode;
		fullscreen?: boolean;
		hidden?: boolean;
		motionAndOrientation?: boolean;
		speechRecognition?: boolean;
	};

	connectAgent?: {
		startingSyncID?: string; // This denotes which part of the proctor script should be executed when the user first connects.
		contactFlowID?: string;
		priority?: UserQueuePriority;
	};
}

// eslint-disable-next-line no-shadow
export enum Theme {
	Light = "light",
	Dark = "dark",
	SheMed = "shemed",
	HeMed = "hemed"
}
